import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { OidcAuthProvider } from '@/auth/oidc-provider'
import { ConfigErrorScreen } from '@/components/ConfigErrorScreen'
import { useAuth } from '@/hooks/useAuth'
import { useClassificationBootstrap } from '@/hooks/useClassification'
import { isCognitoConfigured } from '@/lib/cognito-config'
import { RealtimeProvider } from '@/realtime/RealtimeProvider'
import { routes } from './routes'

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000, // 5 minutes
			retry: 1,
		},
	},
})

const router = createBrowserRouter(routes)

/**
 * Merely mounting the hook starts the session-wide classification bootstrap
 * fetch, so every axis picker across the app finds it already warm in cache.
 * Gated on `isAuthenticated`: this renders on every route including `/login`
 * (it sits above `RouterProvider`), and an unauthenticated request here would
 * 401 — which the Axios interceptor treats as a failed-renewal signal and
 * answers with `bridge.startLogin()`, hijacking a visitor off the sign-in
 * page before they can use it.
 */
function ClassificationBootstrapPrefetch() {
	const { isAuthenticated } = useAuth()
	useClassificationBootstrap({ enabled: isAuthenticated })
	return null
}

function App() {
	// Fail closed (ADR-0011): without Cognito configuration do NOT render
	// the app or authenticate. Show a safe configuration state; never inject a stub
	// identity (e.g. on a personal stage).
	if (!isCognitoConfigured) {
		return <ConfigErrorScreen />
	}

	// QueryClientProvider wraps the OIDC provider so the auth bridge / terminal-state
	// gate inside OidcAuthProvider can use react-query (clear `/users/auth/me` on logout,
	// renewal, etc.).
	//
	// RealtimeProvider sits inside OidcAuthProvider for the same reason — it needs
	// both the token and the query client — and OUTSIDE RouterProvider so the one
	// connection survives navigation instead of reconnecting per route.
	return (
		<QueryClientProvider client={queryClient}>
			<OidcAuthProvider>
				<RealtimeProvider>
					<ClassificationBootstrapPrefetch />
					<RouterProvider router={router} />
				</RealtimeProvider>
			</OidcAuthProvider>
		</QueryClientProvider>
	)
}

export default App
